import React from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';

import { Dropdown, DropdownItemCheckbox, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import Message from 'components/Message';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import { UNCATEGORIZED_KEY } from './SkuItemReports.filters';
import messages from './SkuItemReports.messages';
import { FILTER_TYPE, useReportsTracking } from './SkuReports.tracking';
import './SkuItemReportsLocalFilters.scss';

const { block, element } = bem('sku-item-reports-local-filters');

const toggleValues = (selected, value, isChecked) =>
  isChecked ? [...selected, value] : selected.filter(entry => entry !== value);

const OptionsDropdown = ({ label, options, selected, onChange, onOpen }) => (
  <Dropdown attachment="left" className={element('dropdown')} closeOnSelect={false} onOpen={onOpen}>
    <DropdownToggle>
      <Message message={label} values={{ count: selected.length }} />
    </DropdownToggle>
    <DropdownMenu>
      {options.map(option => (
        <DropdownItemCheckbox
          key={option.value}
          isChecked={selected.includes(option.value)}
          onClick={isChecked => onChange(toggleValues(selected, option.value, isChecked))}>
          {option.label}
        </DropdownItemCheckbox>
      ))}
    </DropdownMenu>
  </Dropdown>
);

OptionsDropdown.propTypes = {
  label: PropTypes.shape({ id: PropTypes.string }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.node.isRequired }))
    .isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
};

const SkuItemReportsLocalFilters = ({
  options,
  categories,
  onCategoriesChange,
  menus,
  onMenusChange,
  search,
  onSearchChange,
  suggestions,
}) => {
  const intl = useIntl();
  const tracking = useReportsTracking();

  const categoryOptions = [
    ...options.categories.map(category => ({ value: category, label: category })),
    ...(options.hasUncategorized
      ? [{ value: UNCATEGORIZED_KEY, label: <Message message={messages.uncategorized} /> }]
      : []),
  ];
  const menuOptions = options.menus.map(menu => ({ value: menu, label: menu }));

  return (
    <div className={block()}>
      <OptionsDropdown
        label={messages.categoryFilterLabel}
        options={categoryOptions}
        selected={categories}
        onChange={onCategoriesChange}
        onOpen={() => tracking.filterOpened(FILTER_TYPE.CATEGORY)}
      />
      <OptionsDropdown
        label={messages.menuFilterLabel}
        options={menuOptions}
        selected={menus}
        onChange={onMenusChange}
        onOpen={() => tracking.filterOpened(FILTER_TYPE.MENU)}
      />
      <div className={element('search')}>
        <Isvg className={element('search-icon')} src={ICONS.searchGlassOutline} />
        <input
          className={element('search-input')}
          type="search"
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          aria-label={intl.formatMessage(messages.searchPlaceholder)}
          value={search}
          onChange={event => onSearchChange(event.target.value)}
        />
        {search.trim().length > 0 && suggestions.length > 0 && (
          <ul className={element('search-suggestions')} aria-label={intl.formatMessage(messages.searchSuggestions)}>
            {suggestions.map(suggestion => (
              <li key={suggestion}>
                <button
                  className={element('search-suggestion')}
                  type="button"
                  onClick={() => onSearchChange(suggestion, { fromSuggestion: true })}>
                  {suggestion}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

SkuItemReportsLocalFilters.propTypes = {
  options: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    menus: PropTypes.arrayOf(PropTypes.string).isRequired,
    hasUncategorized: PropTypes.bool.isRequired,
  }).isRequired,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCategoriesChange: PropTypes.func.isRequired,
  menus: PropTypes.arrayOf(PropTypes.string).isRequired,
  onMenusChange: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default SkuItemReportsLocalFilters;
